import React, { useState, useEffect, useCallback, useRef } from "react"
import { ThemeProvider } from "styled-components"
import { theme } from "./styles/theme"
import GlobalStyle from "./styles/GlobalStyle"
import FilterSection, { FilterValues } from "./components/filters/FilterSection"
import InfluencerList from "./components/influencers/InfluencerList"
import Container from "./components/common/Container"
import { Influencer } from "./types/influencer"
import { DataService } from "./services"
import styled, { css } from "styled-components"
import { useLocation, useNavigate, Routes, Route } from "react-router-dom"
import "./App.css"
import InfluencerResearchToolPage from "./pages/InfluencerResearchTool"
import InfluencerResultsPage from "./pages/InfluencerResultsPage"

/* eslint-disable react-hooks/exhaustive-deps */

const Header = styled.header<{ $isResultsPage?: boolean }>`
  background: ${({ theme }) => theme.colors.backgroundDark};
  padding: ${({ theme }) => theme.spacing[6]} 0
    ${({ theme }) => theme.spacing[5]};
  box-shadow: 0 4px 20px rgba(12, 21, 40, 0.2);
  position: relative;
  overflow: hidden;
  flex-grow: ${({ $isResultsPage }) => ($isResultsPage ? 0 : 1)};
  display: flex;
  flex-direction: column;

  /* Add subtle gradient overlay */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(67, 97, 238, 0.1) 0%,
      rgba(114, 9, 183, 0.05) 100%
    );
    z-index: 0;
  }

  /* Add subtle pattern overlay */
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle at 25% 25%,
      rgba(255, 255, 255, 0.05) 1%,
      transparent 6%
    );
    background-size: 50px 50px;
    z-index: 0;
  }
`

const HeaderContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  cursor: pointer;
`

const LogoLink = styled.a`
  display: flex;
  align-items: center;
`

const HeaderLogoImage = styled.img`
  height: 45px;
  margin-right: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 65px;
  }
`

const HeaderContent = styled.div<{ $isResultsPage?: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2.5rem 1rem 1rem;
  text-align: center;
  position: relative;
  z-index: 2;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    ${({ $isResultsPage }) =>
      !$isResultsPage &&
      css`
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `}
  }

  h1 {
    color: white;
    font-size: 2.2rem;
    font-weight: bold;
    margin-bottom: 1.5rem;
    text-align: center;
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      text-align: left;
      font-size: 1.8rem;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.1rem;
    margin-bottom: 1rem;
    line-height: 1.4;
    text-align: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    h1 {
      font-size: 2.5rem;
      margin-bottom: 1.75rem;
    }

    p {
      font-size: 1.25rem;
    }
  }
`

// Enhanced Trusted By section styles
const TrustedBySection = styled.div`
  text-align: center;
  margin-top: 2rem;
  margin-bottom: 5rem;
  padding: 2rem 0;
  position: relative;
`

const TrustedByText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 2.5rem;
  font-weight: 500;
  position: relative;

  /* Add subtle decorative line */
  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
    letter-spacing: 4px;
  }
`

const ClientLogosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 2.5rem;
  margin: 0 auto;
  padding: 0.5rem 1.5rem;
  max-width: 900px;
  background: rgb(61, 91, 219, 0);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-wrap: nowrap;
    gap: 3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 3.5rem;
    padding: 1rem 1rem;
  }
`

// Individual logo card styling - premium and modern
const LogoCard = styled.div`
  border-radius: 0;
  padding: 0;
  transition: transform 0.3s ease;
  cursor: pointer;
  position: relative;
`

// Logo styling optimized for individual cards
const ClientLogo = styled.img`
  height: 28px;
  max-width: 130px;
  object-fit: contain;
  transition: filter 0.3s ease, opacity 0.3s ease;
  filter: grayscale(0%) opacity(1);
  display: block;

  ${LogoCard}:hover & {
    filter: grayscale(0%) opacity(1);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 32px;
    max-width: 150px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 40px;
    max-width: 170px;
  }
`

const PhilipsLogo = styled(ClientLogo)`
  height: 15px;
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 15px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 22px;
  }
`

const DrOetkerLogo = styled(ClientLogo)`
  height: 22px;
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 26px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 40px;
  }
`

const DoritosLogo = styled(ClientLogo)`
  height: 26px;
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 30px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 40px;
  }
`

const LaysLogo = styled(ClientLogo)`
  height: 26px;
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 30px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 40px;
  }
`

const MainContent = styled.main<{ $isResultsPage?: boolean }>`
  flex-grow: ${({ $isResultsPage }) => ($isResultsPage ? 1 : 0)};
  background-color: ${({ theme, $isResultsPage }) =>
    $isResultsPage ? theme.colors.backgroundAlt : "transparent"};

  ${({ theme, $isResultsPage }) =>
    $isResultsPage
      ? css`
          display: flex; /* Changed from block to flex for better child layout */
          flex-direction: column;
          padding-top: ${theme.spacing[6]};
          padding-bottom: ${theme.spacing[6]};
        `
      : css`
          display: none; /* Hide on landing page */
          padding-top: 0;
          padding-bottom: 0;
        `}
`

const CenteredContainer = styled(Container)`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 1400px; /* Increased width from 1200px to 1400px */
  margin: 0 auto;
  padding-top: ${({ theme }) => theme.spacing[4]};
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FilterContainer = styled.div`
  margin: 0 auto;
  max-width: 100%;
  width: 700px;
`

const Footer = styled.footer`
  background: ${({ theme }) => theme.colors.backgroundDark};
  color: ${({ theme }) => theme.colors.textDark};
  padding: ${({ theme }) => theme.spacing[2]} 0;
  box-shadow: 0 -2px 10px rgba(12, 21, 40, 0.1);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]} 0;
  }
`

const FooterContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
`

const FooterText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  margin-bottom: 0;
  opacity: 0.7;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FullPlatformButton = styled.a`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: 30px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
    background: #3d8bf0;
    color: white;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    bottom: 16px;
    right: 16px;
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ButtonIcon = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.lg};
`

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.backgroundDark};
`

const KeyBenefitsWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing[6]} 0
    ${({ theme }) => theme.spacing[3]};
  padding: 0 ${({ theme }) => theme.spacing[0]};
`

const KeyBenefitsTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`

const KeyBenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${({ theme }) => theme.spacing[5]};
  }
`

const BenefitCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: left;

  h5 {
    font-size: ${({ theme }) => theme.fontSizes.md};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: white;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.5;
    margin-bottom: 0; /* Remove default paragraph margin */
  }
`

const StickyCTAWrapper = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${({ theme }) => theme.spacing[6]}; /* Increased padding */
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  box-shadow: 0 -3px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column; /* Stack text and button */
  align-items: center; /* Center items horizontally */
  justify-content: center;
  z-index: 1000;
  transform: translateY(${({ $isVisible }) => ($isVisible ? "0" : "100%")});
  transition: transform 0.3s ease-in-out, padding 0.3s ease-in-out; /* Added padding transition */
`

const CloseStickyCTAButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing[2]};
  right: ${({ theme }) => theme.spacing[2]};
  background: transparent;
  border: none;
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[1]};
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`

const HighlightPill = styled.span`
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: 50px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  text-align: center;
  display: inline-block;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
    width: 100%;
  }
`

const PillsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
  }
`

const StickyCTAText = styled.p`
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.md};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-align: center;
  max-width: 90%;
`

const StickyCTAButton = styled.button`
  background: white;
  color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: #f0f0f0;
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    background: #e0e0e0;
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`

// Add these new styled components BEFORE the App function, or with other styled components
const HeroLayoutWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr; // Single column by default for mobile
  align-items: flex-start;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1.1fr 0.9fr; // Two columns for large screens
  }
`

const HeroTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 0 20px;
  width: 100%;

  h1,
  p {
    text-align: left;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    align-items: flex-start;
    text-align: left;
    h1,
    p {
      text-align: left;
    }
  }
`

const HeroFormColumn = styled.div`
  padding: 0rem 0rem 0rem 0rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: visible;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0rem 0rem 0rem 3rem;
  }
`

const PeekingFormImage = styled.img`
  display: block; // Show on mobile by default
  width: 80%; // Responsive width for mobile
  //max-width: 400px; // Max width for mobile
  height: auto;
  margin: 0 auto; // Centered, initial vertical margin adjusted by margin-top
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  position: relative; // For z-index and positioning adjustments
  z-index: -1; // To place it behind the form card
  margin-top: -80px; // Pulls the image up to peek from under the form; adjust as needed

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: absolute;
    width: 480px; // Desktop specific width
    max-width: none; // Reset mobile max-width
    height: auto; // Ensure full height on desktop
    bottom: -100px;
    left: 50%;
    transform: translateX(calc(-50% + ${({ theme }) => theme.spacing[6]}));
    margin: 0; // Reset mobile margins and negative margin-top
    z-index: -1; // Retain z-index for desktop layering
    // border-radius is inherited
  }
`

// New Teaser Image styled component
const TeaserImage = styled.img`
  width: 100%;
  height: auto;
  position: absolute;
  left: 0;
`

// New styled component for the teaser section on the landing page
const LandingTeaserSection = styled.section`
  width: 100%;
  overflow: hidden;
  position: relative;
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`

function App() {
  // Add a ref to track initial render
  const isInitialMount = useRef(true)
  const ignoreFetchResultsRef = useRef(false) // Added ref

  const [selectedFilters, setSelectedFilters] = useState<FilterValues>({
    industry: "",
    country: "",
    budget: "",
    businessEmail: "",
    companyName: "",
    phoneNumber: "",
  })

  const [influencers, setInfluencers] = useState<Influencer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState<boolean>(false)
  const [showResults, setShowResults] = useState<boolean>(false)
  const [showStickyCTA, setShowStickyCTA] = useState(false)
  const [isStickyCTADismissed, setIsStickyCTADismissed] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  // Add a ref for the Footer to scroll to
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const footerRef = useRef<HTMLDivElement>(null)

  // Helper function to generate URL with filter parameters (commented out for now)
  // const generateFilterUrl = useCallback((filterValues: FilterValues): string => {
  //   const params = new URLSearchParams();
  //
  //   if (filterValues.industry) {
  //     params.set('industry', filterValues.industry);
  //   }
  //
  //   if (filterValues.country) {
  //     params.set('country', filterValues.country);
  //   }
  //
  //   if (filterValues.budget) {
  //     params.set('budget', filterValues.budget);
  //   }
  //
  //   if (filterValues.businessEmail) {
  //     params.set('email', filterValues.businessEmail);
  //   }
  //
  //   return `?${params.toString()}`;
  // }, []);

  // Define fetchInfluencers with useCallback to prevent it from changing on re-renders
  const fetchInfluencers = useCallback(
    async (filterValues: FilterValues) => {
      setIsLoading(true)

      // --- Helper to get budget range --- Start
      const getBudgetRangeForService = (
        budgetKey: string
      ): { minBudget?: number; maxBudget?: number } => {
        const budgetMap: Record<string, { min: number; max: number }> = {
          "0-3000": { min: 0, max: 2999 },
          "0-4999": { min: 0, max: 4999 }, // For "Up to €5,000"
          "3000-5000": { min: 3000, max: 4999 },
          "5000-10000": { min: 5000, max: 9999 },
          "10000+": { min: 10000, max: Number.MAX_SAFE_INTEGER },
          // Add other legacy keys if the DataService might still receive them directly from old links/storage
          "5000-15000": { min: 5000, max: 14999 },
          "15000-25000": { min: 15000, max: 24999 },
          "25000-50000": { min: 25000, max: 49999 },
          "50000+": { min: 50000, max: Number.MAX_SAFE_INTEGER },
        }
        const range = budgetMap[budgetKey]
        if (range) {
          return { minBudget: range.min, maxBudget: range.max }
        }
        // If budgetKey is empty or not in map, don't send budget params,
        // implying no budget filter from the service perspective, or it uses a default.
        // Or, if a default filter is desired for empty/invalid keys, return it here.
        // For now, returning undefined will omit these from paramsForService if key is unknown.
        return {}
      }
      // --- Helper to get budget range --- End

      const budgetRangeParams = filterValues.budget
        ? getBudgetRangeForService(filterValues.budget)
        : {}

      const paramsForService = {
        industry: filterValues.industry,
        country: filterValues.country,
        businessEmail: filterValues.businessEmail, // Keep other params
        ...(budgetRangeParams.minBudget !== undefined && {
          minBudget: budgetRangeParams.minBudget,
        }),
        ...(budgetRangeParams.maxBudget !== undefined && {
          maxBudget: budgetRangeParams.maxBudget,
        }),
        // Explicitly DO NOT pass filterValues.budget if we are sending minBudget/maxBudget
      }

      try {
        // Pass the transformed params to the DataService
        const response = await DataService.getInfluencers(
          paramsForService as any
        ) // Use 'as any' if DataService.getInfluencers expects FilterValues type strictly

        if (ignoreFetchResultsRef.current) {
          // console.log("Ignoring stale fetch results");
          setIsLoading(false) // Reset loading status
          return
        }

        setInfluencers(response.influencers)
        setHasSubmittedEmail(true)
        setShowResults(true)

        // Save user info and email submission status
        try {
          await DataService.saveUserInfo(
            filterValues.businessEmail,
            filterValues
          )
          localStorage.setItem("emailSubmitted", "true")
        } catch (error) {
          //console.error('Error saving email:', error);
        }

        // Save selected filters to localStorage - these filters led to the current results
        localStorage.setItem("selectedFilters", JSON.stringify(filterValues))

        // REMOVED: navigate(generateFilterUrl(filterValues));
      } catch (error) {
        //console.error('Error fetching influencers:', error);
        // Handle error state here (could add error message state)
      } finally {
        setIsLoading(false)
      }
    },
    [
      /* generateFilterUrl was a dep for the removed navigate */
    ]
  ) // Removed navigate from dependencies

  // Parse and apply URL parameters
  useEffect(() => {
    if (isInitialMount.current) {
      return // Initial load handled by the other useEffect
    }

    const searchParams = new URLSearchParams(location.search)
    const industryParam = searchParams.get("industry")
    const countryParam = searchParams.get("country")
    const budgetParam = searchParams.get("budget")
    const emailParam = searchParams.get("email")
    const companyNameParam = searchParams.get("companyName")
    const phoneNumberParam = searchParams.get("phoneNumber")

    const hasRequiredParamsForResults =
      industryParam && countryParam && budgetParam

    if (isLoading && !hasRequiredParamsForResults) {
      // If navigating back to landing page while a fetch was in progress for results,
      // we should prioritize showing the landing page.
      // However, an immediate setShowResults(false) might race with the fetch completion.
      // This scenario needs careful handling, for now, we let the existing isLoading guard in fetchInfluencers prevent new fetches.
      // The key is that if hasRequiredParamsForResults is false, the else block below MUST run.
    }

    if (hasRequiredParamsForResults) {
      // URL indicates we should be on a results page.
      const newFiltersFromURL = {
        industry: industryParam,
        country: countryParam,
        budget: budgetParam,
        businessEmail: emailParam || selectedFilters.businessEmail, // Preserve existing email if not in URL
        companyName: companyNameParam || selectedFilters.companyName,
        phoneNumber: phoneNumberParam || selectedFilters.phoneNumber,
      }

      // Determine if an update/fetch is needed
      const filtersAreDifferent =
        newFiltersFromURL.industry !== selectedFilters.industry ||
        newFiltersFromURL.country !== selectedFilters.country ||
        newFiltersFromURL.budget !== selectedFilters.budget ||
        newFiltersFromURL.businessEmail !== selectedFilters.businessEmail ||
        newFiltersFromURL.companyName !== selectedFilters.companyName ||
        newFiltersFromURL.phoneNumber !== selectedFilters.phoneNumber

      if (!showResults || filtersAreDifferent) {
        if (!isLoading) {
          // Only fetch if not already loading
          ignoreFetchResultsRef.current = false // This is an intentional fetch based on URL
          setSelectedFilters(newFiltersFromURL)
          fetchInfluencers(newFiltersFromURL) // This function internally sets showResults = true and isLoading correctly
        }
      }
    } else {
      // URL does NOT have required params for results, so we should be on the landing page.
      if (showResults) {
        setShowResults(false) // Critically ensure results are hidden
      }

      // Pre-fill form with any partial params from the URL, or reflect empty state.
      const partialFilters = {
        industry: industryParam || "",
        country: countryParam || "",
        budget: budgetParam || "",
        businessEmail: emailParam || selectedFilters.businessEmail, // Preserve email if not in URL, or if it was empty
        companyName: companyNameParam || selectedFilters.companyName,
        phoneNumber: phoneNumberParam || selectedFilters.phoneNumber,
      }

      // Update selectedFilters state only if they differ from current to avoid unnecessary re-renders
      if (
        selectedFilters.industry !== partialFilters.industry ||
        selectedFilters.country !== partialFilters.country ||
        selectedFilters.budget !== partialFilters.budget ||
        selectedFilters.businessEmail !== partialFilters.businessEmail ||
        selectedFilters.companyName !== partialFilters.companyName ||
        selectedFilters.phoneNumber !== partialFilters.phoneNumber
      ) {
        setSelectedFilters(partialFilters)
      }
    }
    // Dependencies:
    // location.search is the main trigger.
    // fetchInfluencers (stable due to useCallback).
    // isLoading to prevent fetching if one is in progress.
    // selectedFilters and showResults are for comparison to decide if state updates/fetches are needed.
  }, [
    location.search,
    fetchInfluencers,
    isLoading,
    selectedFilters,
    showResults,
  ])

  // Check for returning user with email submitted - only runs once on mount
  useEffect(() => {
    const emailSubmitted = localStorage.getItem("emailSubmitted")
    if (emailSubmitted === "true") {
      setHasSubmittedEmail(true)
    }

    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search)
    const industryParam = searchParams.get("industry")
    const countryParam = searchParams.get("country")
    const budgetParam = searchParams.get("budget")
    const emailParam = searchParams.get("email")
    const companyNameParam = searchParams.get("companyName")
    const phoneNumberParam = searchParams.get("phoneNumber")

    // If we have parameters in URL, use them as initial values
    if (
      industryParam ||
      countryParam ||
      budgetParam ||
      emailParam ||
      companyNameParam ||
      phoneNumberParam
    ) {
      const urlFilters: FilterValues = {
        industry: industryParam || "",
        country: countryParam || "",
        budget: budgetParam || "",
        businessEmail: emailParam || "",
        companyName: companyNameParam || "",
        phoneNumber: phoneNumberParam || "",
      }
      setSelectedFilters(urlFilters)
      // The other useEffect will handle fetching if these params require results
    } else {
      // No URL params on initial load: Only load from localStorage for pre-filling form, don't auto-fetch.
      const savedFilters = localStorage.getItem("selectedFilters")
      if (savedFilters) {
        try {
          const parsedFilters = JSON.parse(savedFilters) as FilterValues
          setSelectedFilters(parsedFilters) // Pre-fill form state
          // Removed auto-fetch:
          // if (emailSubmitted === 'true') {
          //   ignoreFetchResultsRef.current = false; // Ensure this fetch is not ignored
          //   fetchInfluencers(parsedFilters);
          // }
        } catch (error) {
          //console.error('Error parsing saved filters:', error);
        }
      }
    }
    isInitialMount.current = false
  }, []) // Empty dependency array - only runs once on mount

  // Effect to handle sticky CTA visibility
  useEffect(() => {
    const handleScroll = () => {
      // Get breakpoint for mobile
      const mdBreakpoint = parseInt(theme.breakpoints.md.replace("px", ""), 10)
      const scrollThreshold = window.innerWidth < mdBreakpoint ? 1200 : 400 // Dynamic threshold
      const stickyBarEstimatedHeight = 70 // Estimated height in pixels for the sticky CTA bar

      const ctaSection = document.getElementById("upgrade-cta")

      if (ctaSection) {
        const ctaSectionRect = ctaSection.getBoundingClientRect()
        // Show if scrolled past threshold AND main CTA's top is still effectively below the sticky bar's position
        if (
          window.scrollY > scrollThreshold &&
          ctaSectionRect.top > window.innerHeight - stickyBarEstimatedHeight
        ) {
          setShowStickyCTA(true)
        } else {
          setShowStickyCTA(false)
        }
      } else {
        // Fallback if main CTA is not found (e.g., on filter page before results load)
        // Original behavior: show if scrolled past threshold
        if (window.scrollY > scrollThreshold) {
          setShowStickyCTA(true)
        } else {
          setShowStickyCTA(false)
        }
      }
    }

    window.addEventListener("scroll", handleScroll)
    // Call it once on mount to set initial state based on current scroll/view
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [showResults]) // Add showResults as a dependency, so if it changes (and upgrade-cta appears/disappears), we re-evaluate.

  const handleStickyCTAClick = () => {
    const ctaSection = document.getElementById("upgrade-cta")
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: "smooth" })
    }
  }

  const handleDismissStickyCTA = () => {
    setIsStickyCTADismissed(true)
  }

  // Helper function to generate signup URL with filter parameters
  const generateSignupUrl = useCallback(
    (filterValues: FilterValues): string => {
      const params = new URLSearchParams()

      if (filterValues.industry) {
        // Map "other" value to a more descriptive parameter
        if (filterValues.industry === "other") {
          params.set("source_industry", "not_listed")
        } else {
          params.set("source_industry", filterValues.industry)
        }
      }

      if (filterValues.country) {
        // Map "other" value to a more descriptive parameter
        if (filterValues.country === "other") {
          params.set("source_country", "not_listed")
        } else {
          params.set("source_country", filterValues.country)
        }
      }

      if (filterValues.budget) {
        params.set("source_budget", filterValues.budget)
      }

      if (filterValues.businessEmail) {
        params.set("email", filterValues.businessEmail)
      }

      // Add current URL parameters (like UTM params) to the signup URL
      const currentParams = new URLSearchParams(location.search)

      // Merge current params with filter params (filter params take precedence)
      currentParams.forEach((value, key) => {
        if (!params.has(key)) {
          params.set(key, value)
        }
      })

      return `https://business.hypefy.ai/auth/signup${
        params.toString() ? "?" + params.toString() : ""
      }`
    },
    [location.search]
  )

  // Helper function to generate filter URL parameters (commented out for now)
  // const generateFilterUrl = (filterValues: FilterValues): string => {
  //   const searchParams = new URLSearchParams();
  //   searchParams.set('industry', filterValues.industry);
  //   searchParams.set('country', filterValues.country);
  //   searchParams.set('budget', filterValues.budget);
  //   searchParams.set('businessEmail', filterValues.businessEmail);
  //   searchParams.set('companyName', filterValues.companyName);
  //   if (filterValues.phoneNumber) {
  //     searchParams.set('phoneNumber', filterValues.phoneNumber);
  //   }
  //   return searchParams.toString();
  // };

  const handleFilterSubmit = (filterValues: FilterValues) => {
    // Construct new search params
    const searchParams = new URLSearchParams()
    searchParams.set("industry", filterValues.industry)
    searchParams.set("country", filterValues.country)
    searchParams.set("budget", filterValues.budget)
    searchParams.set("businessEmail", filterValues.businessEmail)
    searchParams.set("companyName", filterValues.companyName)
    if (filterValues.phoneNumber) {
      searchParams.set("phoneNumber", filterValues.phoneNumber)
    }

    // Add current URL parameters (like UTM params) to the signup URL
    const currentParams = new URLSearchParams(location.search)

    // Merge current params with filter params (filter params take precedence)
    currentParams.forEach((value, key) => {
      if (!searchParams.has(key)) {
        searchParams.set(key, value)
      }
    })

    // Update URL without reloading page
    navigate({
      pathname: "",
      search: searchParams.toString(),
    })
  }

  const handleChangeFilters = () => {
    ignoreFetchResultsRef.current = true // Signal to ignore ongoing/stale fetches
    // Set showResults to false to show the filter form
    setShowResults(false)
    localStorage.removeItem("selectedFilters") // Clear persisted filters

    // Reset URL to avoid confusion when going back to filter form
    // but keep the filter values for the form
    // Use navigate with replace to avoid adding new history entry
    navigate("", { replace: true })
  }

  // Function to update filters in the form
  const handleChange = (field: keyof FilterValues, value: string) => {
    const updatedFilters = {
      ...selectedFilters,
      [field]: value,
    }

    // Update the filters state
    setSelectedFilters(updatedFilters)

    // If business email is being updated and the value is valid, save it
    if (
      field === "businessEmail" &&
      value &&
      value.includes("@") &&
      value.includes(".")
    ) {
      try {
        // Save the user info without refreshing the list
        DataService.saveUserInfo(value, updatedFilters).catch((error) => {
          // console.error('Error saving email:', error);
        })
      } catch (error) {
        // console.error('Error saving user info:', error);
      }
    }
  }

  // Generate the current signup URL with filter parameters
  // Update to use a function to ensure it always has the latest values
  const getSignupUrl = () => {
    return generateSignupUrl(selectedFilters)
  }

  // Function to scroll to the existing CTA section
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const scrollToCTA = () => {
    // If results are showing, scroll to the CTA section in the InfluencerList component
    if (showResults) {
      const ctaElement = document.querySelector(".cta-section")
      if (ctaElement) {
        ctaElement.scrollIntoView({ behavior: "smooth" })
      }
    } else {
      // If on the filter page, scroll to top where they can enter their criteria
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  // Function to get the readable country and industry names for the title
  const getHeroTitle = () => {
    // If filters have been submitted, show specific title
    if (showResults && selectedFilters.country && selectedFilters.industry) {
      // Get readable country name
      let finalCountryName: string
      switch (selectedFilters.country) {
        case "uk":
          finalCountryName = "UK"
          break
        case "germany":
          finalCountryName = "German"
          break
        case "croatia":
          finalCountryName = "Croatian"
          break
        // Add other specific country mappings if a different form than getReadableFilterName provides is needed for the title
        default:
          // For unlisted countries, use the generally formatted name from getReadableFilterName
          finalCountryName = getReadableFilterName(
            "country",
            selectedFilters.country
          )
      }

      // Get readable industry name
      let finalIndustryName: string
      switch (selectedFilters.industry) {
        case "beauty-cosmetics":
          finalIndustryName = "Beauty" // Specific short form for title
          break
        case "consumer-electronics":
          finalIndustryName = "Consumer Electronics"
          break
        case "food-beverage":
          finalIndustryName = "Food & Beverage"
          break
        case "fashion-lifestyle":
          finalIndustryName = "Fashion & Lifestyle"
          break
        // Add other specific industry mappings if a different form than getReadableFilterName provides is needed for the title
        default:
          // For unlisted industries, use the generally formatted name from getReadableFilterName
          finalIndustryName = getReadableFilterName(
            "industry",
            selectedFilters.industry
          )
      }

      return `Top ${finalCountryName} ${finalIndustryName} Influencers – Optimized for High-Performing Brand Campaigns`
    }

    // Generic title for landing page
    return "Influencers Already Winning In Your Industry"
  }

  // Helper function to get readable names for filter values
  const getReadableFilterName = (key: string, value: string): string => {
    switch (key) {
      case "industry":
        switch (value) {
          case "beauty-cosmetics":
            return "Beauty & Cosmetics"
          case "consumer-electronics":
            return "Consumer Electronics"
          case "food-beverage":
            return "Food & Beverage"
          // Add other industry cases as needed
          default:
            return (
              value
                .replace(/-/g, " ")
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "Selected Industry"
            )
        }
      case "country":
        switch (value) {
          case "uk":
            return "United Kingdom"
          case "germany":
            return "Germany"
          case "italy":
            return "Italy"
          case "united-states":
            return "United States"
          // Add other country cases as needed
          default:
            return (
              value
                .replace(/-/g, " ")
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "Selected Country"
            )
        }
      default:
        return value
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Routes>
        <Route
          path="/influencer-research-tool"
          element={<InfluencerResearchToolPage />}
        />
        <Route path="/influencer-results" element={<InfluencerResultsPage />} />
        <Route
          path="/"
          element={
            <AppWrapper>
              <Header $isResultsPage={showResults}>
                <HeaderContainer maxWidth="1400px">
                  <LogoContainer>
                    <LogoLink
                      href={`https://hypefy.ai?referrer=freemium-influencer-list${
                        location.search
                          ? "&" + location.search.substring(1)
                          : ""
                      }`}
                      target="_blank"
                    >
                      <HeaderLogoImage
                        src="/images/logo_white.png"
                        alt="Hypefy Logo"
                      />
                    </LogoLink>
                  </LogoContainer>
                </HeaderContainer>

                <HeaderContent $isResultsPage={showResults}>
                  {!showResults ? (
                    <HeroLayoutWrapper>
                      <HeroTextColumn>
                        <h1>{getHeroTitle()}</h1>
                        <p>
                          We've curated a list of influencers who've already
                          driven real results in your space. <br /> <br />
                          Hypefy doesn't give you filters because{" "}
                          <em>you don't need filters</em>. We give you a full,
                          done-for-you list of top creators. Enter your details,
                          and we will show you a{" "}
                          <em>ready-to-roll list tailored to your market</em>.
                        </p>
                        <KeyBenefitsWrapper>
                          <KeyBenefitsTitle>
                            Why Marketing Managers Choose Hypefy
                          </KeyBenefitsTitle>
                          <KeyBenefitsGrid>
                            <BenefitCard>
                              <h5>AI-Powered Matching</h5>
                              <p>
                                Find creators your audience actually follows,
                                not just popular profiles.
                              </p>
                            </BenefitCard>
                            <BenefitCard>
                              <h5>Full Campaign Management</h5>
                              <p>
                                From influencer outreach to content approval -
                                our AI handles everything.
                              </p>
                            </BenefitCard>
                            <BenefitCard>
                              <h5>Executive-Ready Reports</h5>
                              <p>
                                Clear performance data that shows real business
                                impact.
                              </p>
                            </BenefitCard>
                          </KeyBenefitsGrid>
                        </KeyBenefitsWrapper>
                        <TrustedBySection>
                          <TrustedByText>
                            Join 1000+ brands that have put their influencer
                            marketing on autopilot
                          </TrustedByText>
                          <ClientLogosContainer>
                            <LogoCard>
                              <ClientLogo
                                src="/images/clients/nivea_logo.svg"
                                alt="Nivea"
                              />
                            </LogoCard>
                            <LogoCard>
                              <DoritosLogo
                                src="/images/clients/doritos_logo.png"
                                alt="Doritos"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LaysLogo
                                src="/images/clients/lays_logo.png"
                                alt="Lays"
                              />
                            </LogoCard>
                            <LogoCard>
                              <PhilipsLogo
                                src="/images/clients/philips.svg"
                                alt="Philips"
                              />
                            </LogoCard>
                            <LogoCard>
                              <DrOetkerLogo
                                src="/images/clients/DrOetker-Logo.png"
                                alt="Dr. Oetker"
                              />
                            </LogoCard>
                          </ClientLogosContainer>
                        </TrustedBySection>
                      </HeroTextColumn>
                      <HeroFormColumn>
                        <FilterSection
                          initialValues={selectedFilters}
                          onFieldChange={handleChange}
                          onSubmit={handleFilterSubmit}
                          isLoading={isLoading}
                          signupUrl={getSignupUrl()}
                        />
                        {!showResults && (
                          <PeekingFormImage
                            src="/images/blurred_list_teaser7.png"
                            alt="List preview"
                          />
                        )}
                      </HeroFormColumn>
                    </HeroLayoutWrapper>
                  ) : (
                    // This is the existing layout for when results ARE shown
                    <>
                      <h1>{getHeroTitle()}</h1>
                      <PillsWrapper>
                        <HighlightPill>
                          ✓ Specific to{" "}
                          {getReadableFilterName(
                            "industry",
                            selectedFilters.industry
                          )}
                        </HighlightPill>
                        <HighlightPill>
                          ✓ With audience in{" "}
                          {getReadableFilterName(
                            "country",
                            selectedFilters.country
                          )}
                        </HighlightPill>
                        <HighlightPill>✓ Campaign-ready</HighlightPill>
                      </PillsWrapper>
                    </>
                  )}
                </HeaderContent>
              </Header>

              <MainContent $isResultsPage={showResults}>
                {!showResults && (
                  <CenteredContainer>
                    <LandingTeaserSection>
                      <TeaserImage
                        src="/images/blurred_list_teaser7.png"
                        alt="Blurred teaser of influencer list"
                      />
                    </LandingTeaserSection>
                  </CenteredContainer>
                )}
                {showResults && (
                  <CenteredContainer>
                    <InfluencerList
                      influencers={influencers}
                      isLoading={isLoading}
                      hasSubmittedEmail={hasSubmittedEmail}
                      currentFilters={selectedFilters}
                      onChangeFilters={handleChangeFilters}
                      signupUrl={getSignupUrl()}
                    />
                  </CenteredContainer>
                )}
              </MainContent>
              {showStickyCTA && showResults && !isStickyCTADismissed && (
                <StickyCTAWrapper $isVisible={showStickyCTA}>
                  <CloseStickyCTAButton onClick={handleDismissStickyCTA}>
                    &times;
                  </CloseStickyCTAButton>
                  <StickyCTAText>
                    <strong>
                      Unlock VIP Access: Get the personalized treatment you
                      deserve!
                    </strong>
                    <br />
                    Sign up and get creators based on your{" "}
                    <em>exact product and goals</em> in minutes - 100% free.{" "}
                    <br />
                  </StickyCTAText>
                  <StickyCTAButton onClick={handleStickyCTAClick}>
                    Sign Up For Free
                  </StickyCTAButton>
                </StickyCTAWrapper>
              )}
              <Footer>
                <FooterContainer>
                  <FooterText>
                    &copy; {new Date().getFullYear()} Hypefy. All rights
                    reserved.
                  </FooterText>
                </FooterContainer>
              </Footer>
            </AppWrapper>
          }
        />
      </Routes>
    </ThemeProvider>
  )
}

export default App
